import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSettings } from "@/lib/useSettings";
import { api, fetchList } from "@/lib/api";
import NewsCard from "@/components/NewsCard";
import JobCardCompact from "@/components/JobCardCompact";
import AlertCard from "@/components/AlertCard";
import InsightCard from "@/components/InsightCard";
import EcosystemCard from "@/components/EcosystemCard";
import TopSignals from "@/components/TopSignals";
import NewsletterBlock from "@/components/NewsletterBlock";
import DataleoPositioning from "@/components/DataleoPositioning";
import HtmlText from "@/components/HtmlText";
import SmartImage from "@/components/SmartImage";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import FreshBadge from "@/components/FreshBadge";
import {
  buildOrganizationSchema,
  buildWebsiteSchema,
  buildLocalBusinessSchema,
} from "@/lib/schema";
import { TOPICS } from "@/lib/topics";

const SectionTitle = ({ eyebrow, title, to, viewAllLabel, testid }) => (
  <div data-testid={testid} className="flex items-end justify-between border-b-2 border-[#111827] pb-2 mb-5">
    <div className="flex items-baseline gap-3">
      <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">{eyebrow}</div>
      <h2 className="font-display text-xl sm:text-2xl text-[#111827] leading-tight">{title}</h2>
    </div>
    {to && (
      <Link to={to} className="text-[12px] font-medium text-[#111827] hover:text-[#00A878] inline-flex items-center gap-1">
        {viewAllLabel} <ArrowRight size={12} />
      </Link>
    )}
  </div>
);

const Ticker = ({ items, lang }) => (
  <div data-testid="hero-ticker" className="bg-white border-b border-[#E5E7EB]">
    <div className="dl-container py-2.5 flex items-center gap-4 overflow-x-auto">
      <span className="shrink-0 inline-flex items-center gap-1.5 font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#00A878] animate-pulse" />
        {lang === "fr" ? "En direct" : "Live radar"}
      </span>
      <div className="flex items-center gap-6">
        {items.map((n, i) => (
          <a
            key={n.id}
            href={n.url || "#"}
            target={n.url ? "_blank" : "_self"}
            rel="noreferrer"
            data-testid={`ticker-item-${i}`}
            className="shrink-0 inline-flex items-baseline gap-2 group"
          >
            <span className="font-mono-dl text-[10px] tracking-wider text-[#9ca3af]">{n.published_at}</span>
            <FreshBadge publishedAt={n.published_at} createdAt={n.created_at} lang={lang} />
            <span className="text-[13px] text-[#111827] group-hover:text-[#00A878] line-clamp-1 max-w-[400px]">
              {n.title}
            </span>
          </a>
        ))}
      </div>
    </div>
  </div>
);

const Home = () => {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const [news, setNews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [insights, setInsights] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [ecosystem, setEcosystem] = useState([]);
  const [topSignals, setTopSignals] = useState(null);

  useEffect(() => {
    // News: 4 for Home (1 featured + 3 secondary) + a few extra for the ticker.
    fetchList("/news", { language: lang, limit: 6 }).then(setNews).catch(() => {});
    fetchList("/jobs", { language: lang, limit: 3 }).then(setJobs).catch(() => {});
    fetchList("/alerts", { language: lang, limit: 4 }).then(setAlerts).catch(() => {});
    fetchList("/insights", { language: lang, limit: 2 }).then(setInsights).catch(() => {});
    fetchList("/tutorials", { language: lang, limit: 3 }).then(setTutorials).catch(() => {});
    // Ecosystem: top 3 most active in the last 7 days (with activity badges).
    api.get("/ecosystem/top-active", { params: { language: lang, days: 7, limit: 3 } })
      .then((r) => setEcosystem(r.data || []))
      .catch(() => {});
    // Top signals of the week (news + insight + alert/job).
    api.get("/home/top-signals", { params: { language: lang } })
      .then((r) => setTopSignals(r.data))
      .catch(() => {});
  }, [lang]);

  const featured = news[0];
  const secondary = news.slice(1, 4); // 3 secondary cards for the right column

  // SEO copy is intentionally 120-160 chars per audit guidance.
  const seoDescEn =
    "Dataleo Supply Chain AI Radar tracks AI, planning, supply chain software and decision systems. Curated insights for operations leaders.";
  const seoDescFr =
    "Dataleo Supply Chain AI Radar suit l’IA, la planification, les logiciels supply chain et les systèmes de décision pour les directions Opérations.";

  return (
    <div data-testid="home-page">
      <SeoHead
        lang={lang}
        path="/"
        title={lang === "fr"
          ? "Dataleo Supply Chain AI Radar | Actualités & Insights"
          : "Dataleo Supply Chain AI Radar | News & Insights"}
        bareTitle
        description={lang === "fr" ? seoDescFr : seoDescEn}
        alternates={{ en: "/", fr: "/?lang=fr" }}
        jsonLd={[
          buildOrganizationSchema({ baseUrl: SITE_URL, settings }),
          buildWebsiteSchema({ baseUrl: SITE_URL }),
          buildLocalBusinessSchema({ baseUrl: SITE_URL, settings }),
        ]}
      />
      {/* Ticker */}
      {news.length > 0 && <Ticker items={news.slice(0, 5)} lang={lang} />}

      {/* Editorial band — distinct dark teal background */}
      <section data-testid="hero-section" className="bg-[#0e1f1a] text-white border-y-2 border-[#00A878]">
        <div className="dl-container py-6 sm:py-7 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#FFD400]">
              {t.hero.band1}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mt-2 max-w-3xl">
              {t.hero.band2}
            </h1>
          </div>
          <p className="text-[13px] text-[#9ca3af] max-w-md leading-relaxed">
            {t.hero.subtitle}
          </p>
        </div>
      </section>

      {/* 01 / Top signals — curated 3-up of the week */}
      <TopSignals data={topSignals} lang={lang} />

      {/* 02 / Latest news — 1 featured + 3 secondary, capped at 4 total */}
      <section className="dl-container pt-6 pb-12">
        <SectionTitle
          testid="latest-news-section"
          eyebrow="02 / News"
          title={t.sections.latestNews}
          to="/news"
          viewAllLabel={t.sections.viewAll}
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[440px]">
          <div className="lg:col-span-7">
            {featured ? (
              <article data-testid="featured-news" className="group bg-white border border-[#E5E7EB] hover:border-[#111827] transition-colors p-5">
                <div className="flex items-center gap-2 text-[10px] font-mono-dl tracking-wider uppercase text-[#4B5563]">
                  <span className="text-[#00A878]">{featured.relevance_area}</span>
                  <span className="text-[#9ca3af]">·</span>
                  <span>{featured.published_at}</span>
                  <FreshBadge publishedAt={featured.published_at} createdAt={featured.created_at} lang={lang} />
                  <span className={`ml-auto px-2 py-0.5 impact-${featured.impact_level}`}>
                    {t.impact[featured.impact_level] || featured.impact_level}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl leading-[1.1] text-[#111827] mt-3">
                  <Link
                    to={`/news/${featured.id}`}
                    data-testid="featured-news-title"
                    className="group-hover:text-[#00A878] transition-colors"
                  >
                    {featured.title}
                  </Link>
                </h3>
                <HtmlText html={featured.summary} className="text-[14px] leading-relaxed text-[#4B5563] mt-2 line-clamp-2" />
                <Link
                  to={`/news/${featured.id}`}
                  className="mt-4 block relative overflow-hidden aspect-[21/9] bg-[#F3F4F6]"
                >
                  <SmartImage
                    entity={featured}
                    type="news"
                    alt={featured.title || "Featured news"}
                    className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
                {featured.dataleo_angle && (
                  <div className="mt-4 border-l-2 border-[#00A878] pl-3">
                    <div className="text-[10px] font-mono-dl tracking-wider uppercase text-[#00A878]">{t.labels.dataleoAngle}</div>
                    <HtmlText html={featured.dataleo_angle} className="text-[13px] text-[#111827] mt-0.5" />
                  </div>
                )}
              </article>
            ) : (
              <div aria-hidden="true" className="border border-[#E5E7EB] bg-[#F7F7F2] min-h-[420px]" />
            )}
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4" data-testid="news-secondary-column">
            {secondary.map((n, i) => (
              <NewsCard key={n.id} item={n} idx={i + 1} layout="horizontal" />
            ))}
          </div>
        </div>
      </section>

      {/* 03 / Featured insight */}
      {insights[0] && (
        <section className="dl-container pb-12">
          <SectionTitle
            testid="featured-section"
            eyebrow="03 / Featured"
            title={t.sections.featured}
            to="/insights"
            viewAllLabel={t.sections.viewAll}
          />
          <InsightCard item={insights[0]} idx={0} featured />
        </section>
      )}

      {/* 04 / Jobs (compact, max 3)  +  05 / Alerts & Events (max 4) — single consolidated block */}
      <section className="dl-container pb-12 grid lg:grid-cols-2 gap-10">
        <div data-testid="jobs-section">
          <SectionTitle eyebrow="04 / Jobs" title={t.sections.latestJobs} to="/jobs" viewAllLabel={t.sections.viewAll} />
          <div className="flex flex-col gap-3">
            {jobs.slice(0, 3).map((j, i) => <JobCardCompact key={j.id} item={j} idx={i} />)}
          </div>
        </div>
        <div data-testid="alerts-section">
          <SectionTitle
            eyebrow="05 / Alerts & Events"
            title={lang === "fr" ? "Alertes & événements" : "Alerts & events"}
            to="/alerts"
            viewAllLabel={t.sections.viewAll}
          />
          <div className="grid sm:grid-cols-2 gap-3">
            {alerts.slice(0, 4).map((a, i) => <AlertCard key={a.id} item={a} idx={i} compact />)}
          </div>
        </div>
      </section>

      {/* 06 / Tutorials */}
      <section data-testid="tutorials-section" className="dl-container pb-14">
        <SectionTitle eyebrow="06 / How-to" title={t.sections.latestTutorials} to="/tutorials" viewAllLabel={t.sections.viewAll} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tutorials.map((tu, i) => (
            <Link
              key={tu.id}
              to={`/tutorials/${tu.id}`}
              data-testid={`home-tutorial-${i}`}
              className="group bg-white border border-[#E5E7EB] hover:border-[#111827] transition-colors overflow-hidden"
            >
              <div className="aspect-[16/9] bg-[#F3F4F6] overflow-hidden">
                <SmartImage src={tu.featured_image} entity={tu} type="tutorial" alt={tu.title || "Tutorial cover"} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-4">
                <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#00A878] flex items-center gap-2">
                  <span>{tu.difficulty} · {tu.duration_minutes} {t.sections.minutes}</span>
                  <FreshBadge publishedAt={tu.published_at} createdAt={tu.created_at} lang={lang} />
                </div>
                <h3 className="font-display text-lg mt-1 leading-snug text-[#111827] group-hover:text-[#00A878]">{tu.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 07 / Ecosystem — top 3 by activity (last 7 days) with badges */}
      <section data-testid="ecosystem-section" className="dl-container pb-14">
        <SectionTitle
          testid="ecosystem-section-title"
          eyebrow="07 / Ecosystem"
          title={lang === "fr" ? "Écosystème — les plus actifs" : "Most active in the ecosystem"}
          to="/ecosystem"
          viewAllLabel={t.sections.viewAll}
        />
        <p className="text-[13px] text-[#4B5563] -mt-2 mb-5 max-w-2xl">
          {lang === "fr"
            ? "Les 3 acteurs avec le plus d'activité (actualités, recrutements, alertes) sur les 7 derniers jours."
            : "The 3 players with the strongest activity (news, jobs, alerts) over the last 7 days."}
        </p>
        {ecosystem.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ecosystem.slice(0, 3).map((e, i) => (
              <EcosystemCard key={e.id} entity={e} idx={i} lang={lang} />
            ))}
          </div>
        ) : (
          <div className="text-[13px] text-[#9ca3af] italic">
            {lang === "fr" ? "Aucune entrée publiée pour le moment." : "No entries published yet."}
          </div>
        )}
      </section>

      {/* 08 / Topics */}
      <section data-testid="topics-section" className="bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="dl-container py-10">
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">08 / Topics</div>
          <h2 className="font-display text-2xl sm:text-3xl mt-1 text-[#111827] leading-tight">
            {t.sections.topics}
          </h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {TOPICS.map((topic) => {
              const label = lang === "fr" ? topic.fr : topic.en;
              return (
                <Link
                  key={topic.slug}
                  to={`/topic/${topic.slug}`}
                  data-testid={`topic-${topic.slug}`}
                  className="px-3 py-1.5 bg-white border border-[#E5E7EB] hover:border-[#111827] text-[13px] transition-colors"
                >
                  {label}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <div id="newsletter">
        <NewsletterBlock />
      </div>

      {/* Contribute CTA strip */}
      <section data-testid="contribute-cta" className="bg-[#FFD400] text-[#111827] border-y-2 border-[#111827]">
        <div className="dl-container py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase">{lang === "fr" ? "Contribuer" : "Contribute"}</div>
            <h3 className="font-display text-xl sm:text-2xl mt-1 leading-tight">
              {lang === "fr" ? "Vous avez un signal IA pour les Opérations à partager ?" : "Got an AI signal worth sharing for Operations?"}
            </h3>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Link to="/submit" data-testid="cta-submit" className="bg-[#111827] text-white hover:bg-[#1F2937] px-4 py-2.5 text-sm font-bold">
              {lang === "fr" ? "Soumettre une news →" : "Submit a news →"}
            </Link>
            <Link to="/contact" data-testid="cta-contact" className="border-2 border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-white px-4 py-2 text-sm font-bold transition-colors">
              {lang === "fr" ? "Contact" : "Contact"}
            </Link>
          </div>
        </div>
      </section>

      <DataleoPositioning />
    </div>
  );
};

export default Home;
